import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@store'
import { postService } from '@services/api/posts'
import apiClient from '@services/api/client'
import toast from 'react-hot-toast'
import { CATEGORIES } from '@constants/categories'
import { ROUTES } from '@constants'
import { storageURL, mediaUrl } from '@config'

function EditVideoPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    videoUrl: '',
    price: '',
    location: '',
    condition: '',
    thumbnailImage: null,
    thumbnailFile: null,
    currentThumbnailUrl: '',
    currentVideoUrl: '',
    newVideoFile: null,
    newVideoName: ''
  })

  const categories = CATEGORIES

  const conditions = [
    { value: 'neuf', label: 'Neuf' },
    { value: 'scelle', label: 'Scellé' },
    { value: 'reconditionne', label: 'Reconditionné' },
    { value: 'quasi_neuf', label: 'Quasi neuf' },
    { value: 'excellent', label: 'Excellent' },
    { value: 'bon', label: 'Bon état' },
    { value: 'moyen', label: 'État moyen' },
    { value: 'usage', label: 'Usagé' },
  ]

  useEffect(() => {
    fetchVideo()
  }, [id])

  const fetchVideo = async () => {
    try {
      setLoading(true)
      const res = await postService.getPostById(id)
      const p = res.data || res

      if (p && p.post_id) {
        const videoFile = p.fichiers?.find(f => /\.(mp4|mov|avi|webm)$/i.test(f.chemin))
        const thumbFile = p.fichiers?.find(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.chemin))
        setFormData({
          name: p.libele || '',
          category: p.post_categorie_id ? String(p.post_categorie_id) : '',
          description: p.description || '',
          videoUrl: '',
          price: p.prix ? String(parseFloat(p.prix)) : '',
          location: p.siteWeb || '',
          condition: '',
          thumbnailImage: null,
          thumbnailFile: null,
          currentThumbnailUrl: thumbFile ? mediaUrl(thumbFile.chemin) : '',
          currentVideoUrl: videoFile ? mediaUrl(videoFile.chemin) : ''
        })
      } else {
        toast.error('Vidéo introuvable')
        navigate(ROUTES.DASHBOARD)
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la vidéo:', error)
      toast.error('Erreur lors du chargement')
      navigate(ROUTES.DASHBOARD)
    } finally {
      setLoading(false)
    }
  }

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData({ ...formData, thumbnailImage: URL.createObjectURL(file), thumbnailFile: file })
    } else {
      toast.error('Veuillez sélectionner une image valide')
    }
  }

  const handleVideoUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('video/')) {
      setFormData({ ...formData, newVideoFile: file, newVideoName: file.name })
    } else {
      toast.error('Veuillez sélectionner un fichier vidéo valide')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name || !formData.category || !formData.description || !formData.price) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    setIsSubmitting(true)

    try {
      let response
      const hasFiles = formData.newVideoFile || formData.thumbnailFile

      if (hasFiles) {
        // Avec fichier(s) : POST + _method=PUT (PHP ne parse pas multipart/PUT)
        const formDataToSend = new FormData()
        formDataToSend.append('_method', 'PUT')
        formDataToSend.append('libele', formData.name)
        formDataToSend.append('description', formData.description)
        formDataToSend.append('siteWeb', formData.location)
        formDataToSend.append('prix', formData.price.toString().replace(/[^0-9]/g, ''))
        if (formData.newVideoFile) formDataToSend.append('files[]', formData.newVideoFile)
        if (formData.thumbnailFile) formDataToSend.append('files[]', formData.thumbnailFile)
        response = await apiClient.post(`/posts/${id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        // Sans fichier : PUT JSON
        response = await apiClient.put(`/posts/${id}`, {
          libele:      formData.name,
          description: formData.description,
          siteWeb:     formData.location,
          prix:        formData.price.toString().replace(/[^0-9]/g, ''),
        })
      }

      if (response.success) {
        toast.success('Vidéo modifiée avec succès !')
        navigate(ROUTES.DASHBOARD)
      } else {
        throw new Error(response.message || 'Erreur inconnue')
      }
      
    } catch (error) {
      console.error('Erreur lors de la modification:', error)
      toast.error('Erreur lors de la modification de la vidéo')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fefcf8] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fefcf8]">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 lg:p-12">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-[#001F3F]">Modifier la vidéo</h1>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Titre de votre vidéo"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent appearance-none bg-white"
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.filter(cat => cat !== 'Toutes les catégories').map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                      Prix<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="50.000 FCFA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                      État
                    </label>
                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent appearance-none bg-white"
                    >
                      <option value="">Sélectionnez l'état</option>
                      {conditions.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Localisation
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Abidjan, Cocody"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    URL de la vidéo
                  </label>
                  <input
                    type="url"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleChange}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">YouTube, Vimeo, ou lien direct</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Description détaillée de votre produit..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent resize-none"
                    required
                  />
                </div>
              </div>

              {/* Colonne droite - Vidéo + Miniature */}
              <div className="space-y-6">

                {/* Prévisualisation de la vidéo actuelle */}
                {/* Vidéo actuelle */}
                {formData.currentVideoUrl && !formData.newVideoFile && (
                  <div>
                    <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                      Vidéo actuelle
                    </label>
                    <div className="rounded-xl overflow-hidden border-2 border-gray-200 bg-black">
                      <video
                        src={formData.currentVideoUrl}
                        controls
                        className="w-full max-h-64 object-contain"
                        onError={(e) => { e.target.parentElement.innerHTML = '<p class="text-white text-center py-8 text-sm">Impossible de lire la vidéo</p>' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Vidéo actuellement publiée</p>
                  </div>
                )}

                {/* Nouvelle vidéo sélectionnée */}
                {formData.newVideoFile && (
                  <div>
                    <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                      Nouvelle vidéo sélectionnée
                    </label>
                    <div className="rounded-xl overflow-hidden border-2 border-[#D4AF37] bg-black">
                      <video
                        src={URL.createObjectURL(formData.newVideoFile)}
                        controls
                        className="w-full max-h-64 object-contain"
                      />
                    </div>
                    <p className="text-xs text-[#D4AF37] mt-1 font-medium">✓ {formData.newVideoName}</p>
                  </div>
                )}

                {/* Zone de changement de vidéo */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    {formData.currentVideoUrl ? 'Remplacer la vidéo' : 'Ajouter une vidéo'}
                  </label>
                  <label htmlFor="newVideo" className="block w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#D4AF37] hover:bg-yellow-50 transition-colors">
                    <input
                      type="file"
                      id="newVideo"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                    <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                    </svg>
                    <p className="text-sm font-medium text-[#001F3F]">
                      {formData.newVideoFile ? 'Changer la vidéo' : 'Cliquez pour sélectionner une nouvelle vidéo'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">MP4, MOV, AVI — max 50 MB</p>
                  </label>
                  {formData.newVideoFile && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, newVideoFile: null, newVideoName: '' }))}
                      className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
                    >
                      ✕ Annuler le changement de vidéo
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Miniature de la vidéo
                  </label>
                  
                  {(formData.thumbnailImage || formData.currentThumbnailUrl) && (
                    <div className="mb-4">
                      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                        <img
                          src={formData.thumbnailImage || formData.currentThumbnailUrl}
                          alt="Aperçu"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.style.display = 'none' }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.thumbnailFile ? 'Nouvelle miniature sélectionnée' : 'Miniature actuelle'}
                      </p>
                    </div>
                  )}

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#D4AF37] transition-colors cursor-pointer">
                    <input
                      type="file"
                      id="thumbnail"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      className="hidden"
                    />
                    <label htmlFor="thumbnail" className="cursor-pointer">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600 mb-1">Changer la miniature</p>
                      <p className="text-sm text-gray-500">Format recommandé: 1280x720px</p>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-4 justify-end pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Modification...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditVideoPage
